/**
 * Pure helpers for the Validation Rules page.
 *
 * YAML parsing (rule-pack upload), API response mappers, error parsers, and
 * the semantic-intelligence classifier live here so the page component can
 * import them without carrying the algorithms in its own file.
 *
 * Everything here is side-effect-free and framework-free - just data in,
 * data out - which keeps it trivial to test.
 */
import { ERROR_CODE_MESSAGES, LAYER_META_BY_ID } from "./constants";
import type {
  ApiErrorEntry,
  IntelligenceLayerId,
  ParsedApiError,
  RuleParams,
  RuleSeverity,
  UnpublishedRuleItem,
  UnpublishedRulePack,
  ValidationRule,
} from "./types";

// ─── YAML parsing (rule-pack upload) ────────────────────────────────────────

/** A significant YAML line, with its indentation preserved. */
interface YamlLine {
  indent: number;
  text: string;
}

/** Strips surrounding quotes and any trailing `# comment` from a scalar. */
function cleanYamlValue(raw: string): string {
  let v = raw.trim();
  if (!v.startsWith('"') && !v.startsWith("'")) {
    const hash = v.indexOf(" #");
    if (hash >= 0) v = v.slice(0, hash).trim();
  }
  return v.replace(/^["']|["']$/g, "").trim();
}

/** Splits `key: value`. Returns null for list items and bare scalars. */
function splitYamlKeyValue(
  text: string,
): { key: string; value: string } | null {
  const idx = text.indexOf(":");
  if (idx < 0) return null;
  const key = text.slice(0, idx).trim();
  if (!key) return null;
  return { key, value: cleanYamlValue(text.slice(idx + 1)) };
}

/** First entry of an inline flow list such as `[REQUIREMENT, DOMAIN]`. */
function firstFlowListValue(raw: string): string {
  const inner = raw.replace(/^\[|\]$/g, "");
  return cleanYamlValue(inner.split(",")[0] ?? "");
}

/**
 * The catalogue grades rules HIGH / MEDIUM / LOW, while the rules table only
 * knows MANDATORY and STANDARD. Anything at or above HIGH is mandatory.
 */
export function toRuleSeverity(raw: string | undefined): RuleSeverity {
  const v = (raw ?? "").trim().toUpperCase();
  if (v === "STANDARD" || v === "MEDIUM" || v === "LOW" || v === "INFO")
    return "STANDARD";
  return "MANDATORY";
}

/**
 * Builds one rule from the lines of a single `rules:` list item.
 *
 * Handles the catalogue shape, where the fields live under nested `metadata:`
 * and `remediation:` maps, as well as the older flat shape that puts
 * `ruleCode` / `title` directly on the item. Everything under `evaluation:` and
 * `applicability:` is deliberately ignored - those are match conditions, not
 * rule identity, and reading them is what made the previous parser emit
 * phantom rules.
 */
function ruleFromYamlBlock(
  block: YamlLine[],
  index: number,
): UnpublishedRuleItem | null {
  if (block.length === 0) return null;
  const base = Math.min(...block.map((l) => l.indent));

  const flat: Record<string, string> = {};
  const meta: Record<string, string> = {};
  const remediation: Record<string, string> = {};
  let intelligence = "";
  let section = "";

  for (const line of block) {
    if (line.indent === base) {
      const kv = splitYamlKeyValue(line.text);
      if (!kv) continue;
      // A key with no value opens a nested map; one with a value is a scalar.
      section = kv.value ? "" : kv.key;
      if (!kv.value) continue;
      flat[kv.key] = kv.value;
      if (kv.key === "intelligence") intelligence = firstFlowListValue(kv.value);
      continue;
    }
    if (line.indent !== base + 2) continue; // deeper: evaluation internals
    if (section === "intelligence") {
      if (!intelligence && line.text.startsWith("- "))
        intelligence = cleanYamlValue(line.text.slice(2));
      continue;
    }
    const kv = splitYamlKeyValue(line.text);
    if (!kv) continue;
    if (section === "metadata") meta[kv.key] = kv.value;
    else if (section === "remediation") remediation[kv.key] = kv.value;
  }

  const ruleCode = meta.id || flat.ruleCode || flat.code || flat.id || "";
  const title = meta.name || flat.title || flat.name || "";
  if (!ruleCode && !title) return null;

  return {
    id: ruleCode || `rule-${index + 1}`,
    ruleCode: ruleCode || `R-${String(index + 1).padStart(3, "0")}`,
    title: title || `Rule ${index + 1}`,
    category: (intelligence || flat.category || "semantic").toLowerCase(),
    severity: toRuleSeverity(meta.severity || flat.severity),
    remediation:
      remediation.recommendation || remediation.title || flat.remediation || "",
    description: meta.description || flat.description || "",
  };
}

/**
 * Collects the list items directly beneath the line at `startIdx`, returning
 * one block of lines per item.
 */
function collectYamlListItems(
  lines: YamlLine[],
  startIdx: number,
  parentIndent: number,
): YamlLine[][] {
  const blocks: YamlLine[][] = [];
  let i = startIdx;
  while (i < lines.length && lines[i].indent > parentIndent) {
    if (!lines[i].text.startsWith("- ")) {
      i++;
      continue;
    }
    const itemIndent = lines[i].indent;
    const block: YamlLine[] = [
      { indent: itemIndent + 2, text: lines[i].text.slice(2).trim() },
    ];
    let j = i + 1;
    while (j < lines.length && lines[j].indent > itemIndent) {
      block.push(lines[j]);
      j++;
    }
    blocks.push(block);
    i = j;
  }
  return blocks;
}

/** Fallback pack shown when a YAML upload contains no parseable rules. */
const FALLBACK_YAML_RULES: UnpublishedRuleItem[] = [
  {
    id: "rule-1",
    ruleCode: "SEM-001",
    title: "Document Metadata & Header Check",
    category: "metadata",
    severity: "MANDATORY",
    remediation:
      "Ensure document headers include author, version, and classification tags.",
    description:
      "Validates header attributes across uploaded governance documents.",
  },
  {
    id: "rule-2",
    ruleCode: "SEM-002",
    title: "Compliance Clause Structure Verification",
    category: "structural",
    severity: "STANDARD",
    remediation:
      "Structure compliance sections using standard numbering (1.1, 1.2).",
    description:
      "Checks for required section headers and sub-clause formatting.",
  },
];

export function parseYamlRules(
  text: string,
  fileName: string,
): { packName: string; rules: UnpublishedRuleItem[] } {
  let packName = fileName.replace(/\.(yml|yaml)$/i, "");
  const rules: UnpublishedRuleItem[] = [];

  try {
    const lines: YamlLine[] = text
      .split(/\r?\n/)
      .map((raw) => {
        const expanded = raw.replace(/\t/g, "  ");
        const trimmed = expanded.trim();
        return {
          indent: expanded.length - expanded.trimStart().length,
          text: trimmed,
        };
      })
      .filter((l) => l.text && !l.text.startsWith("#"));

    // Catalogue name, from the top-level `metadata: name:`.
    const metaIdx = lines.findIndex(
      (l) => l.indent === 0 && l.text === "metadata:",
    );
    if (metaIdx >= 0) {
      for (let j = metaIdx + 1; j < lines.length && lines[j].indent > 0; j++) {
        const kv = splitYamlKeyValue(lines[j].text);
        if (kv?.key === "name" && kv.value) {
          packName = kv.value;
          break;
        }
      }
    }

    // Every `rules:` sequence in the file, at any nesting depth.
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].text !== "rules:") continue;
      for (const block of collectYamlListItems(lines, i + 1, lines[i].indent)) {
        const rule = ruleFromYamlBlock(block, rules.length);
        if (rule) rules.push(rule);
      }
    }

    // No `rules:` key: treat the shallowest list in the file as the rule list.
    if (rules.length === 0) {
      const firstItem = lines.find((l) => l.text.startsWith("- "));
      if (firstItem) {
        for (const block of collectYamlListItems(
          lines,
          0,
          firstItem.indent - 1,
        )) {
          const rule = ruleFromYamlBlock(block, rules.length);
          if (rule) rules.push(rule);
        }
      }
    }
  } catch {
    /* Malformed YAML - fall through to the fallback pack below. */
  }

  if (rules.length === 0) rules.push(...FALLBACK_YAML_RULES);

  return { packName, rules };
}

// ─── API response mappers ──────────────────────────────────────────────────

/**
 * Pulls the pack id out of an upload response.
 *
 * The envelope nests the pack differently depending on the endpoint version
 * (`data.id`, `data.rulePack.id`, ...), so check the known shapes rather than
 * assuming one. Returns null when the response carries no id - the caller
 * must not invent one.
 */
export function extractRulePackId(response: unknown): string | null {
  const r = (response ?? {}) as Record<string, any>;
  const envelope = (r.data ?? r) as Record<string, any>;
  const candidates: unknown[] = [
    /* Current shape: { data: { summary: { id, status, format, ruleCount } } } */
    envelope?.summary?.id,
    envelope?.summary?.packId,
    envelope?.data?.summary?.id,
    envelope?.id,
    envelope?.packId,
    envelope?.rulePackId,
    envelope?.semanticRulePackId,
    envelope?.rulePack?.id,
    envelope?.pack?.id,
    envelope?.semanticRulePack?.id,
    envelope?.data?.id,
    r?.id,
  ];
  for (const c of candidates) {
    const v = typeof c === "string" ? c.trim() : c == null ? "" : String(c);
    if (v) return v;
  }
  return null;
}

/**
 * Rule total to show for a pack. Prefers the server's `ruleCount`, since the
 * upload response omits the rule bodies and the locally parsed `rules` list
 * is only a preview of the file.
 */
export function packRuleCount(pack: UnpublishedRulePack): number {
  return typeof pack.ruleCount === "number" ? pack.ruleCount : pack.rules.length;
}

// ─── Semantic intelligence classification ──────────────────────────────────

export function normalizeIntelligence(
  raw: unknown,
): IntelligenceLayerId | undefined {
  if (typeof raw !== "string") return undefined;
  const v = raw.trim().toUpperCase();
  if (v in LAYER_META_BY_ID) return v as IntelligenceLayerId;
  return undefined;
}

/**
 * Heuristic classifier used for rules whose API payload does not include an
 * `intelligence` field. Uses a scored lookup across the rule's code prefix,
 * category, title, target, and validator type to pick the best-fitting layer.
 * Returns undefined only when no signal at all matches - those rows still
 * render, they simply appear with "—" under Layer.
 */
export function classifyRuleLayer(rule: {
  ruleCode?: string;
  category?: string;
  title?: string;
  target?: string;
  validatorType?: string;
}): IntelligenceLayerId | undefined {
  const hay = [
    rule.ruleCode ?? "",
    rule.category ?? "",
    rule.title ?? "",
    rule.target ?? "",
    rule.validatorType ?? "",
  ]
    .join(" ")
    .toLowerCase();

  const codePrefix = (rule.ruleCode ?? "")
    .toUpperCase()
    .split(/[-_.\s]/)[0]
    .replace(/^SEM$/, "");

  // Explicit code-prefix wins over keyword scoring.
  const prefixMap: Record<string, IntelligenceLayerId> = {
    DOM: "DOMAIN",
    DMN: "DOMAIN",
    REQ: "REQUIREMENT",
    RQ: "REQUIREMENT",
    CPT: "CONCEPT",
    CNC: "CONCEPT",
    REL: "RELATIONSHIP",
    RLN: "RELATIONSHIP",
    CST: "CONSISTENCY",
    CNS: "CONSISTENCY",
    CONS: "CONSISTENCY",
    KNW: "KNOWLEDGE",
    KQ: "KNOWLEDGE",
    KNOW: "KNOWLEDGE",
    GOV: "GOVERNANCE",
    GVN: "GOVERNANCE",
    POL: "GOVERNANCE",
  };
  if (codePrefix && prefixMap[codePrefix]) return prefixMap[codePrefix];

  const scores: Record<IntelligenceLayerId, number> = {
    DOMAIN: 0,
    REQUIREMENT: 0,
    CONCEPT: 0,
    RELATIONSHIP: 0,
    CONSISTENCY: 0,
    KNOWLEDGE: 0,
    GOVERNANCE: 0,
  };

  const bump = (layer: IntelligenceLayerId, weight: number) => {
    scores[layer] += weight;
  };

  // DOMAIN: business/topic area, header/section presence, filename convention.
  ["domain", "topic", "section", "header", "structure", "chapter"].forEach(
    (kw) => hay.includes(kw) && bump("DOMAIN", 2),
  );
  // REQUIREMENT: MUST/SHALL modality, requirement IDs.
  ["requirement", "must", "shall", "modality", "obligation", "req id"].forEach(
    (kw) => hay.includes(kw) && bump("REQUIREMENT", 2),
  );
  // CONCEPT: synonyms, aliases, terminology, glossary.
  [
    "concept",
    "synonym",
    "alias",
    "term",
    "terminology",
    "glossary",
    "definition",
  ].forEach((kw) => hay.includes(kw) && bump("CONCEPT", 2));
  // RELATIONSHIP: mapping, linkage, dependency.
  [
    "relation",
    "mapping",
    "link",
    "reference",
    "cross-reference",
    "dependency",
  ].forEach((kw) => hay.includes(kw) && bump("RELATIONSHIP", 2));
  // CONSISTENCY: duplicates, contradictions, coverage, count.
  [
    "consist",
    "duplicate",
    "contradiction",
    "coverage",
    "min_match_ratio",
    "count",
    "unique",
  ].forEach((kw) => hay.includes(kw) && bump("CONSISTENCY", 2));
  // KNOWLEDGE: evidence, reference, citation, accuracy, freshness.
  [
    "knowledge",
    "evidence",
    "citation",
    "accuracy",
    "freshness",
    "confidence",
    "quality",
  ].forEach((kw) => hay.includes(kw) && bump("KNOWLEDGE", 2));
  // GOVERNANCE: policy, control, compliance, filename convention, review.
  [
    "governance",
    "policy",
    "control",
    "compliance",
    "audit",
    "approval",
    "review",
    "filename",
    "naming",
    "metadata",
  ].forEach((kw) => hay.includes(kw) && bump("GOVERNANCE", 2));

  let best: IntelligenceLayerId | undefined;
  let bestScore = 0;
  (Object.keys(scores) as IntelligenceLayerId[]).forEach((k) => {
    if (scores[k] > bestScore) {
      bestScore = scores[k];
      best = k;
    }
  });
  return best;
}

/**
 * Normalizes rules from GET /content-registry/semantic-rules into the same
 * ValidationRule shape used by the org rules table. The semantic-rules API
 * uses a different field set (`code`, `name`, `enabled`, `intelligence`,
 * `severity` in HIGH/MEDIUM/LOW), so we map those fields into ours.
 */
export function normalizeSemanticRules(
  raw: unknown[],
  orgId: string,
): ValidationRule[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r: any, i: number): ValidationRule | null => {
      if (!r || typeof r !== "object") return null;
      const layer = normalizeIntelligence(r.intelligence);
      const ruleCode = String(r.code ?? r.ruleId ?? "").trim();
      const title = String(r.name ?? r.title ?? "").trim();
      if (!ruleCode && !title) return null;
      const rawSev = String(r.severity ?? "")
        .trim()
        .toUpperCase();
      const severity: RuleSeverity =
        rawSev === "STANDARD" ||
        rawSev === "MEDIUM" ||
        rawSev === "LOW" ||
        rawSev === "INFO"
          ? "STANDARD"
          : "MANDATORY";
      const enabled = r.enabled !== false;
      const validatorType = String(r.type ?? "semantic").toLowerCase();
      const target = String(r.scope ?? "").toLowerCase();
      const category = layer ? layer.toLowerCase() : "semantic";
      const finalLayer =
        layer ??
        classifyRuleLayer({
          ruleCode,
          category,
          title,
          target,
          validatorType,
        });
      return {
        id: String(r.ruleId ?? ruleCode ?? `sem-${i}`),
        orgId,
        kind: "SYSTEM",
        ruleCode: ruleCode || `SEM-${String(i + 1).padStart(3, "0")}`,
        title: title || `Semantic rule ${i + 1}`,
        validatorType,
        target,
        params: {},
        severity,
        remediation:
          typeof r.recommendation === "object" && r.recommendation
            ? String(
                r.recommendation.summary ?? r.recommendation.detail ?? "",
              )
            : "",
        category,
        enabled,
        status: enabled ? "ACTIVE" : "INACTIVE",
        createdAt: String(r.audit?.createdAt ?? ""),
        updatedAt: String(r.audit?.updatedAt ?? ""),
        intelligence: finalLayer,
      };
    })
    .filter((x): x is ValidationRule => x !== null);
}

// ─── API error parsing ─────────────────────────────────────────────────────

function pointerToFieldName(pointer?: string): string | undefined {
  if (!pointer) return undefined;
  const parts = pointer.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : undefined;
}

export function parseApiError(err: unknown): ParsedApiError {
  const fieldErrors: Record<string, string> = {};
  const e = err as any;
  const errors: ApiErrorEntry[] = e?.response?.data?.errors ?? [];

  if (errors.length === 0) {
    return {
      banner:
        e?.message === "OFFLINE"
          ? "You appear to be offline. Check your connection and try again."
          : (e?.message ?? "Something went wrong. Please try again."),
      fieldErrors,
    };
  }

  let banner = "";
  for (const entry of errors) {
    if (entry.code === "SYS-0002-E") {
      const f = pointerToFieldName(entry.pointer);
      if (f) {
        fieldErrors[f] = entry.detail;
        continue;
      }
    }
    if (!banner) banner = ERROR_CODE_MESSAGES[entry.code] ?? entry.detail;
  }
  if (!banner && Object.keys(fieldErrors).length > 0) {
    banner = "Please fix the highlighted fields and try again.";
  }
  return { banner, fieldErrors };
}

// ─── Rule param helpers ────────────────────────────────────────────────────

/** Shallow equality for a params object - used to detect "no changes". */
export function paramsEqual(a: RuleParams, b: RuleParams): boolean {
  return JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});
}

/**
 * Returns true when the rule's params satisfy the minimum requirements
 * for its validatorType. Used to flag rules that need setup before they
 * can do useful work.
 */
export function isParamsConfigured(
  validatorType: string,
  params: RuleParams,
): boolean {
  const p = (params ?? {}) as any;
  switch (validatorType) {
    case "synonym_contains":
      return Array.isArray(p.aliases) && p.aliases.length > 0;
    case "regex_match":
      return typeof p.pattern === "string" && p.pattern.trim().length > 0;
    case "count_gte":
      return typeof p.min === "number" && p.min > 0;
    case "text_consistency":
      return typeof p.min_match_ratio === "number" && p.min_match_ratio > 0;
    case "structure_exists":
    case "date_present":
      return true;
    default:
      return true;
  }
}
